<template>
  <div class="p-5 text-xs font-mono select-none min-w-max max-w-max mx-auto">
    <h1 class="text-2xl text-center mb-5">🛡️ Sensoreal Server Monitor</h1>

    <div class="text-2xs opacity-50 mb-4 text-center">
      Last updated: <span class="select-text">{{ lastUpdated || "⏳" }}</span>
      <span class="ml-3" v-if="isLoading">⏳ updating…</span>
    </div>

    <div class="text-2xs opacity-80 mb-4 text-center">
      <span class="select-text">
        {{ summary.total }} servers •
        <span class="ok">OK: {{ summary.ok }}</span> •
        <span class="warn">Not deployed: {{ summary.notDeployed }}</span> •
        <span class="warn">Unreachable: {{ summary.unreachable }}</span> •
        <span class="warn">Unauthorized: {{ summary.unauthorized }}</span> •
        <span class="warn">Other errors: {{ summary.otherErrors }}</span>
      </span>
    </div>

    <table class="table-auto text-center">
      <thead>
        <tr class="bg-white">
          <th class="px-2 sticky top-0 bg-white text-left">Server</th>
          <th class="px-2 sticky top-0 bg-white">Status</th>
          <th class="px-2 sticky top-0 bg-white">Load</th>
          <th class="px-2 sticky top-0 bg-white">Memory</th>
          <th class="px-2 sticky top-0 bg-white">Disk /</th>
          <th class="px-2 sticky top-0 bg-white">Uptime</th>
          <th class="px-2 sticky top-0 bg-white">Fail2Ban</th>
          <th class="px-2 sticky top-0 bg-white">Current IPs Banned</th>
          <th class="px-2 sticky top-0 bg-white">Total IPs Banned</th>
        </tr>
      </thead>

      <tbody>
        <tr
          v-for="server in displayServers"
          :key="server.name"
          class="bg-gray-200 odd:bg-gray-100"
        >
          <td class="px-2 text-left select-text">
            {{ server.name }}
          </td>

          <td class="px-2 text-left">
            <span class="emoji mr-1">{{ statusEmoji(server) }}</span>
            <span class="text-2xs opacity-70 select-text">
              {{ server.error || "OK" }}
            </span>
          </td>

          <td
            class="px-2 text-right select-text"
            :class="metricClass(server, 'load')"
          >
            {{ loadLabel(server) }}
          </td>

          <td
            class="px-2 text-right select-text"
            :class="metricClass(server, 'mem')"
          >
            {{ memLabel(server) }}
          </td>

          <td
            class="px-2 text-right select-text"
            :class="metricClass(server, 'disk')"
          >
            {{ diskLabel(server) }}
          </td>

          <td class="px-2 text-right select-text">
            {{ uptimeLabel(server) }}
          </td>

          <td class="px-2">
            <span class="emoji mr-1">{{ fail2banEmoji(server) }}</span>
            <span class="select-text">{{ fail2banLabel(server) }}</span>
          </td>

          <td class="px-2 text-right select-text">
            {{ num(server, "sshd-invaliduser", "currently_banned") }}
          </td>

          <td class="px-2 text-right select-text">
            {{ num(server, "sshd-invaliduser", "total_banned") }}
          </td>
        </tr>
      </tbody>
    </table>

    <div class="mt-5 text-center">
      <button
        class="border-solid border-black border-2 px-3 py-1 hover:bg-gray-200"
        @click="refresh()"
      >
        Refresh
      </button>
    </div>
  </div>
</template>

<script>
import axios from "axios";
const REFRESH_TIMEOUT_SEC = 30;

export default {
  data() {
    return {
      servers: [],
      isLoading: false,
      lastUpdated: "",
      timer: null,
      destroyed: false,
    };
  },

  computed: {
    displayServers() {
      return this.servers || [];
    },

    summary() {
      const total = (this.servers || []).length;

      let ok = 0,
        notDeployed = 0,
        unreachable = 0,
        unauthorized = 0,
        otherErrors = 0;

      (this.servers || []).forEach(s => {
        const cat = this.statusCategory(s);
        if (cat === "ok") ok++;
        else if (cat === "not_deployed") notDeployed++;
        else if (cat === "unreachable") unreachable++;
        else if (cat === "unauthorized") unauthorized++;
        else otherErrors++;
      });

      return { total, ok, notDeployed, unreachable, unauthorized, otherErrors };
    },
  },

  mounted() {
    this.scheduleRefresh(0);
  },

  beforeDestroy() {
    this.destroyed = true;
    if (this.timer) clearTimeout(this.timer);
  },

  methods: {
    scheduleRefresh(delay) {
      if (this.destroyed) return;

      this.timer = setTimeout(async () => {
        await this.refresh();
        this.scheduleRefresh(REFRESH_TIMEOUT_SEC * 1000);
      }, delay);
    },

    async refresh() {
      if (this.isLoading) return;

      this.isLoading = true;

      try {
        const res = await axios.get("/api/security", {
          timeout: 10000,
        });

        const payload = res.data || {};

        this.servers = payload.servers || [];
        this.lastUpdated = payload.updatedAt
          ? new Date(payload.updatedAt).toLocaleString()
          : "";
      } catch (e) {
        console.error("Failed to fetch /api/security", e);
      } finally {
        this.isLoading = false;
      }
    },

    jail(server, jailName) {
      return server?.data?.jails?.[jailName] || null;
    },

    num(server, jailName, field) {
      const j = this.jail(server, jailName);
      if (!j) return "-";
      if (j.enabled === false) return "—";
      const v = j[field];
      return typeof v === "number" ? v : "-";
    },

    fail2banEmoji(server) {
      const active = server?.data?.fail2ban?.active;
      if (active === true) return "✅";
      if (active === false) return "🚨";
      return "⏳";
    },

    fail2banLabel(server) {
      const active = server?.data?.fail2ban?.active;
      if (active === true) return "active";
      if (active === false) return "inactive";
      return "unknown";
    },

    statusEmoji(server) {
      return server.error ? "🚨" : "✅";
    },

    system(server) {
      return server?.data?.system || null;
    },

    loadLabel(server) {
      const sys = this.system(server);
      const l = sys?.load;
      if (!l) return "-";
      const f = n => (typeof n === "number" ? n.toFixed(2) : "-");
      return `${f(l["1m"])}/${f(l["5m"])}/${f(l["15m"])}`;
    },

    memLabel(server) {
      const sys = this.system(server);
      const m = sys?.mem_mb;
      if (
        !m ||
        typeof m.total !== "number" ||
        typeof m.used !== "number" ||
        m.total <= 0
      ) {
        return "-";
      }

      const pct = Math.round((m.used / m.total) * 100);
      return `${m.used}/${m.total} MB (${pct}%)`;
    },

    diskLabel(server) {
      const sys = this.system(server);
      const d = sys?.disk_root;
      if (!d || typeof d.total_mb !== "number" || typeof d.used_mb !== "number")
        return "-";
      const pct = d.use_pct ? ` (${d.use_pct})` : "";
      return `${d.used_mb}/${d.total_mb} MB${pct}`;
    },

    uptimeLabel(server) {
      const sys = this.system(server);
      const s = sys?.uptime_s;
      if (typeof s !== "number" || !isFinite(s)) return "-";

      const sec = Math.floor(s);
      const days = Math.floor(sec / 86400);
      const hrs = Math.floor((sec % 86400) / 3600);
      const mins = Math.floor((sec % 3600) / 60);

      if (days > 0) return `${days}d ${hrs}h`;
      if (hrs > 0) return `${hrs}h ${mins}m`;
      return `${mins}m`;
    },

    statusCategory(server) {
      if (!server?.error) return "ok";

      const e = String(server.error || "").toLowerCase();

      if (e.includes("http 404")) return "not_deployed";
      if (e.includes("http 401") || e.includes("http 403"))
        return "unauthorized";

      if (
        e.includes("timeout") ||
        e.includes("econnrefused") ||
        e.includes("enotfound") ||
        e.includes("temporary failure in name resolution") ||
        e.includes("network error")
      ) {
        return "unreachable";
      }

      return "other_error";
    },

    currentBans(server) {
      const j = this.jail(server, "sshd-invaliduser");
      const v = j?.currently_banned;
      return typeof v === "number" ? v : null;
    },

    memPct(server) {
      const m = this.system(server)?.mem_mb;
      if (
        !m ||
        typeof m.used !== "number" ||
        typeof m.total !== "number" ||
        m.total <= 0
      ) {
        return null;
      }
      return (m.used / m.total) * 100;
    },

    diskPct(server) {
      const d = this.system(server)?.disk_root;
      if (!d) return null;

      if (typeof d.use_pct === "string" && d.use_pct.endsWith("%")) {
        const n = Number(d.use_pct.replace("%", ""));
        return Number.isFinite(n) ? n : null;
      }

      if (
        typeof d.used_mb === "number" &&
        typeof d.total_mb === "number" &&
        d.total_mb > 0
      ) {
        return (d.used_mb / d.total_mb) * 100;
      }

      return null;
    },

    loadPerCore(server) {
      const l = this.system(server)?.load;
      const one = l?.["1m"];
      return typeof one === "number" ? one : null;
    },

    metricClass(server, metric) {
      if (server?.error) return "";

      if (metric === "disk") {
        const p = this.diskPct(server);
        if (p === null) return "";
        if (p >= 95) return "cell-bad";
        if (p >= 85) return "cell-warn";
        return "cell-ok";
      }

      if (metric === "mem") {
        const p = this.memPct(server);
        if (p === null) return "";
        if (p >= 90) return "cell-bad";
        if (p >= 80) return "cell-warn";
        return "cell-ok";
      }

      if (metric === "load") {
        const one = this.loadPerCore(server);
        if (one === null) return "";
        if (one >= 4) return "cell-bad";
        if (one >= 2) return "cell-warn";
        return "cell-ok";
      }

      return "";
    },
  },
};
</script>

<style>
.emoji {
  font-family: "emoji";
}

.cell-ok {
  color: #111;
}

.cell-warn {
  color: #8a5a00;
  font-weight: 600;
}

.cell-bad {
  color: #b00020;
  font-weight: 700;
}

.ok {
  color: #0b6b0b;
  font-weight: 600;
}

.warn {
  color: #8a5a00;
  font-weight: 600;
}
</style>
